/**
 * 应用入口
 * ---------------------------------------------------------------------------
 * Provider 的嵌套顺序有讲究：
 *   QueryClientProvider  最外层，数据层不依赖任何东西
 *   ToastProvider        次之，让 AuthProvider 抛出的错误也能弹提示
 *   BrowserRouter        路由
 *   AuthProvider         必须在路由内部，因为登录失效时要跳转
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from '@/app/routes';
import { AuthProvider } from '@/lib/auth/auth-context';
import { ToastProvider } from '@/components/ui/toast';
import { createQueryClient } from '@/lib/query-client';
import '@/styles/global.css';

const queryClient = createQueryClient();

const container = document.getElementById('root');
if (!container) {
  throw new Error('找不到 #root 挂载点，检查 index.html');
}

/*
  注册 Service Worker（public/sw.js，只缓存静态资源，不碰接口）。

  **只在生产环境注册。** 开发时注册会拦掉 Vite 的模块请求，
  改了代码看不到效果，还很难查到是 SW 干的。

  注册失败不做任何处理：SW 是纯增强，装不上（旧浏览器、非 HTTPS、
  用户禁用）时应用照常运行，不该因此弹错误打扰员工。
*/
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  </StrictMode>,
);
